import axios from "axios";

import { handlePasskeySignIn, handlePasskeySignInInit, handlePasskeyVerification } from "../hooks/usePasskeyManager";

import { BACKEND_URL } from "./ApiConstants";

let isRedirecting = false;
let isReauthenticating = false;
let failedQueue = [];
const MAX_PASSKEY_RETRIES = 2;
let passkeyRetryCount = 0;

const axiosInstance = axios.create({
  baseURL: BACKEND_URL,
  headers: {
    "Content-Type": "application/json",
    ...(process.env.NODE_ENV !== "production" && { application: "BANKCO" }),
  },
  withCredentials: true,
});

// --- HELPER: PROCESS QUEUE ---
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// --- HELPER: LOGOUT ---
export const forceLogout = (intentional = false) => {
  if (isRedirecting) return;

  // Guard: If we are already on the home page and logged out, don't loop
  const hasToken = localStorage.getItem('token');
  if (!hasToken && window.location.pathname === '/') {
    return;
  }

  isRedirecting = true;
  isReauthenticating = false;
  passkeyRetryCount = 0;

  const authKeys = [
    'token', 'isPasskeyPresent', 'fingerprintToken', 'fingerprintCredentialId',
    'fingerprintRpId', 'fingerprintVisitorId', 'fingerprintOrigin', 'fingerprintPhone'
  ];
  authKeys.forEach(key => localStorage.removeItem(key));
  sessionStorage.removeItem('passkeyToken');

  if (intentional) {
    localStorage.setItem('logout-event', Date.now().toString());
  }

  window.dispatchEvent(new CustomEvent('forceLogout'));

  setTimeout(() => {
    if (window.location.pathname !== '/') {
        window.location.href = '/';
    } else {
        window.location.reload();
    }
    isRedirecting = false;
  }, 100);
};

// Handle generic error status
const handleErrorStatus1 = () => {
  if (isRedirecting) return;
  isRedirecting = true;
  localStorage.setItem('showSignUpAfterRedirect', 'true');
  const showSignUpFlag = localStorage.getItem('showSignUpAfterRedirect');
  forceLogout();
  localStorage.setItem('showSignUpAfterRedirect', showSignUpFlag);

  setTimeout(() => {
    isRedirecting = false;
  }, 100);
};

// --- CORE AUTH FUNCTION ---
const attemptPasskeyAuth = async () => {
  try {
    const init = await handlePasskeySignInInit();
    const assertion = await handlePasskeyVerification(init);
    const result = await handlePasskeySignIn(assertion);

    if (result?.success && result?.token) {
      return { success: true, token: result.token };
    }
    throw new Error("Passkey auth returned unsuccessful result");
  } catch (error) {
    const errorMsg = (error.message || error.toString()).toLowerCase();
    const errorName = error.name;

    if (
      errorName === 'NotAllowedError' ||
      errorMsg.includes('not allowed') ||
      errorMsg.includes('gesture') ||
      errorMsg.includes('interaction')
    ) {
      console.warn("Passkey blocked by browser (waiting for user click).");
      return { success: false, reason: 'BLOCKED' };
    }

    if (errorMsg.includes('cancelled') || errorName === 'AbortError') {
      return { success: false, reason: 'CANCELLED' };
    }

    return { success: false, reason: 'ERROR' };
  }
};

// --- AXIOS INTERCEPTORS ---
axiosInstance.interceptors.request.use(async (config) => {
  const passkeyToken = sessionStorage.getItem("passkeyToken");
  const regularToken = localStorage.getItem("token");

  if (passkeyToken) {
    config.headers.Authorization = `Bearer ${passkeyToken}`;
  } else if (regularToken) {
    config.headers.Authorization = `Bearer ${regularToken}`;
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;
    const response = error.response;

    // --- CONDITION 1: 403 Forbidden -> LOGOUT ---
    if (response?.status === 403) {
      if (response?.data?.error_status === 1) {
        forceLogout();
        return Promise.reject(error);
      }
    }

    // --- CONDITION 2: 401 Unauthorized ---
    if (response?.status === 401) {

      // SUB-CONDITION: 401 + error_status: 1 -> LOGOUT
      if (response?.data?.error_status === 1) {
        forceLogout();
        return Promise.reject(error);
      }

      const isPasskeyPresent = localStorage.getItem("isPasskeyPresent") === "true";
      const hasRegularToken = !!localStorage.getItem("token");

      if (!hasRegularToken) {
        return Promise.reject(error);
      }

      // If passkey missing locally, just reject (do not force logout here to follow strict rules)
      if (!isPasskeyPresent) {
        return Promise.reject(error);
      }

      if (isReauthenticating) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      // Retry Limit: If retried and still 401 (but not error_status:1), just reject.
      if (originalRequest._retryCount >= 1) {
        return Promise.reject(error);
      }
      originalRequest._retryCount = 1;

      isReauthenticating = true;

      const result = await attemptPasskeyAuth();

      if (result.success && result.token) {
        sessionStorage.setItem("passkeyToken", result.token);
        passkeyRetryCount = 0;
        isReauthenticating = false;

        processQueue(null, result.token);
        originalRequest.headers.Authorization = `Bearer ${result.token}`;
        return axiosInstance(originalRequest);
      }

      else if (result.reason === 'BLOCKED') {
        isReauthenticating = false;
        processQueue(new Error("Browser blocked passkey"), null);
        return Promise.reject(error);
      }

      else {
        isReauthenticating = false;
        passkeyRetryCount++;
        if (passkeyRetryCount >= MAX_PASSKEY_RETRIES) {
          processQueue(new Error("Max retries exceeded"), null);
          // Just reject, don't force logout
        } else {
          processQueue(new Error("Passkey cancelled"), null);
        }

        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

// --- INITIAL CHECK ---
export const initialPasskeyReauthCheck = async () => {
  const isPasskeyPresent = localStorage.getItem("isPasskeyPresent") === "true";
  const hasRegularToken = !!localStorage.getItem("token");
  const hasPasskeyToken = !!sessionStorage.getItem("passkeyToken");

  if (isPasskeyPresent && hasRegularToken && !hasPasskeyToken) {
    if (isReauthenticating) return;

    await new Promise(resolve => setTimeout(resolve, 500));

    isReauthenticating = true;
    const result = await attemptPasskeyAuth();

    if (result.success && result.token) {
      sessionStorage.setItem("passkeyToken", result.token);
      passkeyRetryCount = 0;
      processQueue(null, result.token);
    } else {
      processQueue(new Error("Initial check skipped"), null);
    }

    isReauthenticating = false;
  }
};

// --- API WRAPPERS ---
const apiRequest = async (method, url, data = null, config = {}) => {
  try {
    const response = await axiosInstance({ method, url, data, ...config });
    // Safe check: ensure response exists
    if (response && response.data && response.data.error_status === 1) {
      handleErrorStatus1();
      throw new Error("Authentication error");
    }
    return response;
  } catch (error) {
    // Safe check: ensure error.response exists
    if (error.response && error.response.data && error.response.data.error_status === 1) {
      handleErrorStatus1();
    }

    // Only log if it's NOT a 401 (since 401 is handled/logged by interceptor)
    if (error.response?.status !== 401) {
      console.error(`Error with ${method.toUpperCase()} request to ${url}:`, error);
    }
    throw error.response ? error.response : new Error("Network Error");
  }
};

// --- EXPORTS FOR API METHODS ---
export const GET = (url, config = {}) => apiRequest("get", url, null, config);

export const POST = (url, data, config = {}) => apiRequest("post", url, data, config);

export const PUT = (url, data, config = {}) => apiRequest("put", url, data, config);

export const PATCH = (url, data, config = {}) => apiRequest("patch", url, data, config);

export const DELETE = (url, data, config = {}) => apiRequest("delete", url, data, config);

export default axiosInstance;
